import { useSearchParams } from 'react-router-dom';

const HTTP_PROTOCOL_REGEX = /^https?:\/\//i;
const SINGLE_LEADING_SLASH_REGEX = /^\/(?!\/)/;

export const RETURN_TO_SEARCH_PARAM = 'returnTo';

type BuildReturnToSearchParams = {
  returnTo: string | null;
};

type GetSafeReturnToPathParams = {
  allowlist?: readonly string[];
  returnTo: string | null;
};

type SetReturnToSearchParamParams = {
  returnTo: string | null;
  searchParams: URLSearchParams;
};

type UseSafeReturnToOptions = {
  allowlist?: readonly string[];
};

const isSafeInternalPath = (path: string): boolean => {
  return SINGLE_LEADING_SLASH_REGEX.test(path) && !HTTP_PROTOCOL_REGEX.test(path);
};

export const getSafeReturnToPath = (params: GetSafeReturnToPathParams): string | null => {
  const { allowlist, returnTo } = params;

  if (!returnTo) {
    return null;
  }

  if (allowlist?.includes(returnTo)) {
    return returnTo;
  }

  return isSafeInternalPath(returnTo) ? returnTo : null;
};

export const buildReturnToSearch = (params: BuildReturnToSearchParams): string => {
  const { returnTo } = params;

  if (!returnTo) {
    return '';
  }

  return `?${RETURN_TO_SEARCH_PARAM}=${encodeURIComponent(returnTo)}`;
};

export const setReturnToSearchParam = (params: SetReturnToSearchParamParams): void => {
  const { returnTo, searchParams } = params;

  if (!returnTo) {
    return;
  }

  searchParams.set(RETURN_TO_SEARCH_PARAM, returnTo);
};

export const useSafeReturnTo = (
  options: UseSafeReturnToOptions = {},
): {
  returnTo: string | null;
  returnToSearch: string;
} => {
  const { allowlist } = options;
  const [searchParams] = useSearchParams();
  const rawReturnTo = searchParams.get(RETURN_TO_SEARCH_PARAM);
  const returnTo = getSafeReturnToPath({ allowlist, returnTo: rawReturnTo });
  const returnToSearch = buildReturnToSearch({ returnTo });

  return { returnTo, returnToSearch };
};
